import React, { useEffect, useState } from "react";
import Icon from "./Icon";
import { authenticationApi } from "../../features/authentication";
import "../../styles/ai/ai-share-dialog.css";

const shareUrl = (token) => `${window.location.origin}/shared/${token}`;

/**
 * Share a chat as a public, read-only link. Nothing is published until the
 * user presses "Create public link"; the link is a snapshot, so later
 * messages stay private until they press "Update link".
 */
export default function ShareChatDialog({ chat, onClose, onShareChange }) {
    const [token, setToken] = useState(chat.shareToken || null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);
    const [updated, setUpdated] = useState(false);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const run = async (fn) => {
        setBusy(true);
        setError("");
        try {
            await fn();
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const share = () => run(async () => {
        const res = await authenticationApi.shareChatSession(chat.backendId);
        if (token) setUpdated(true);
        setToken(res.token);
        onShareChange(chat.id, res.token);
    });

    const unshare = () => run(async () => {
        await authenticationApi.unshareChatSession(chat.backendId);
        setToken(null);
        setUpdated(false);
        onShareChange(chat.id, null);
    });

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl(token));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setError("Couldn't copy automatically -- select the link and copy it.");
        }
    };

    const title = chat.title?.trim() || "Untitled Chat";

    return (
        <div className="share-overlay" onClick={onClose}>
            <div
                className="share-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="share-dialog-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="share-dialog-head">
                    <h4 id="share-dialog-title">Share conversation</h4>
                    <button type="button" className="share-close" aria-label="Close" onClick={onClose}>
                        <Icon name="x" size={16} />
                    </button>
                </div>

                <p className="share-chat-title">{title}</p>

                {token ? (
                    <>
                        <div className="share-link-row">
                            <Icon name="link" size={15} className="share-link-icon" />
                            <input
                                className="share-link-input"
                                readOnly
                                value={shareUrl(token)}
                                onFocus={(e) => e.target.select()}
                                aria-label="Share link"
                            />
                            <button type="button" className="share-primary" onClick={copy} disabled={busy}>
                                <Icon name={copied ? "check" : "copy"} size={14} />
                                {copied ? "Copied" : "Copy link"}
                            </button>
                        </div>
                        <p className="share-note">
                            {updated ? "Link updated with the latest messages. " : ""}
                            Anyone with this link can view the conversation as it was when you shared it.
                            New messages aren't included until you update the link.
                        </p>
                        <div className="share-actions">
                            <button type="button" className="share-secondary" onClick={share} disabled={busy}>
                                <Icon name="retry" size={14} />
                                Update link
                            </button>
                            <button type="button" className="share-danger" onClick={unshare} disabled={busy}>
                                Stop sharing
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="share-note">
                            This creates a public, read-only link to this conversation. Anyone with the link can
                            see its messages and property results, including any names or personal details
                            written in it.
                        </p>
                        <div className="share-actions">
                            <button type="button" className="share-secondary" onClick={onClose} disabled={busy}>
                                Cancel
                            </button>
                            <button type="button" className="share-primary" onClick={share} disabled={busy}>
                                <Icon name="link" size={14} />
                                {busy ? "Creating…" : "Create public link"}
                            </button>
                        </div>
                    </>
                )}

                {error && <p className="share-error" role="alert">{error}</p>}
            </div>
        </div>
    );
}
